<template>
  <UInputTime
    v-model="timeValue"
    :locale="$i18n.locale.value"
    :hour-cycle="is24hr ? '24' : '12'"
    :disabled="disabled"
    :readonly="readonly"
    :size="uiSize"
    color="neutral"
    class="w-full"
    :class="$attrs.class"
  >
    <template v-if="$slots.leading" #leading>
      <slot name="leading" />
    </template>

    <template v-if="$slots.trailing || showPicker" #trailing>
      <slot name="trailing">
        <UPopover
          v-model:open="pickerOpen"
          :content="{ side: 'bottom', align: 'end', sideOffset: 8, collisionPadding: 12 }"
        >
          <UButton
            color="neutral"
            variant="link"
            size="sm"
            square
            icon="i-lucide-clock"
            :aria-label="$t('general.time')"
            tabindex="-1"
          />

          <template #content>
            <div class="grid gap-1 p-1" :class="is24hr ? 'w-32 grid-cols-2' : 'w-48 grid-cols-3'">
              <div class="max-h-56 overflow-y-auto" aria-label="Hour">
                <button
                  v-for="option in hourOptions"
                  :key="option.value"
                  type="button"
                  class="flex h-8 w-full items-center justify-center rounded-md px-2 text-sm tabular-nums focus:outline-none"
                  :class="pickerButtonClass(option.active, option.disabled)"
                  :disabled="option.disabled"
                  :aria-pressed="option.active"
                  @click="selectHour(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>

              <div class="max-h-56 overflow-y-auto" aria-label="Minute">
                <button
                  v-for="option in minuteOptions"
                  :key="option.value"
                  type="button"
                  class="flex h-8 w-full items-center justify-center rounded-md px-2 text-sm tabular-nums focus:outline-none"
                  :class="pickerButtonClass(option.active, option.disabled)"
                  :disabled="option.disabled"
                  :aria-pressed="option.active"
                  @click="selectMinute(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>

              <div v-if="!is24hr" class="max-h-56 overflow-y-auto" aria-label="Period">
                <button
                  v-for="option in periodOptions"
                  :key="option.value"
                  type="button"
                  class="flex h-8 w-full items-center justify-center rounded-md px-2 text-sm focus:outline-none"
                  :class="pickerButtonClass(option.active, option.disabled)"
                  :disabled="option.disabled"
                  :aria-pressed="option.active"
                  @click="selectPeriod(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </template>
        </UPopover>
      </slot>
    </template>
  </UInputTime>
</template>

<script setup lang="ts">
import { Time } from '@internationalized/date'
import { UButton, UPopover } from '#components'
import { augmenters } from '@/composables/useFieldAugmenters'
import { TimeFormat } from '~/_api/models/location'

const { $auth, $i18n } = useNuxtApp()

interface Props {
  modelValue?: string | Date | number | null
  augmenter?: Record<string, any> | boolean
  picker?: boolean
  pickerInterval?: number
  minTime?: string | Date | number | null
  maxTime?: string | Date | number | null
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  augmenter: () => augmenters.minutes,
  picker: true,
  pickerInterval: 1,
  minTime: null,
  maxTime: null,
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  leading: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'py-2px px-2px text-sm',
  largeSizeClass: 'px-4 text-lg',
  smallSizeClass: 'px-2 text-sm',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value?: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

defineOptions({ inheritAttrs: false })

const pickerOpen = ref(false)

const showPicker = computed(() => props.picker && !props.readonly && !props.disabled)

const uiSize = computed(() => {
  if (props.size === 'sm') return 'sm'
  if (props.size === 'lg') return 'lg'
  return 'md'
})

const is24hr = computed(() => {
  const format = ($auth as any)?.info?.location?.timeFormat
  return format && format === TimeFormat.twentyFourHour
})

const usesMinutesAugmenter = computed(() => props.augmenter === augmenters.minutes)

const normalizedPickerInterval = computed(() => {
  const interval = Number(props.pickerInterval)
  if (!Number.isFinite(interval) || interval <= 0) return 1
  return Math.min(Math.max(Math.round(interval), 1), 60)
})

const minOptionMinutes = computed(() => normalizeOptionBoundary(props.minTime, 0))
const maxOptionMinutes = computed(() => normalizeOptionBoundary(props.maxTime, 1439))

const selectedMinutes = computed(() => valueToMinutes(props.modelValue))

const pickerBaseMinutes = computed(() => {
  if (selectedMinutes.value === null) return minOptionMinutes.value
  if (selectedMinutes.value === 1440) return 0
  return selectedMinutes.value
})

const selectedHour24 = computed(() => Math.floor(pickerBaseMinutes.value / 60))
const selectedMinute = computed(() => pickerBaseMinutes.value % 60)
const selectedPeriod = computed(() => selectedHour24.value >= 12 ? 'PM' : 'AM')

const hourOptions = computed(() => {
  if (!is24hr.value) {
    return Array.from({ length: 12 }, (_, index) => {
      const value = index + 1
      const hour24 = hour12To24(value, selectedPeriod.value)

      return {
        value,
        label: String(value),
        active: hour24 === selectedHour24.value,
        disabled: !hasAllowedMinuteForHour(hour24),
      }
    })
  }

  return Array.from({ length: 24 }, (_, value) => ({
    value,
    label: zeroPad(value),
    active: value === selectedHour24.value,
    disabled: !hasAllowedMinuteForHour(value),
  }))
})

const minuteOptions = computed(() => {
  const interval = normalizedPickerInterval.value
  const values = new Set<number>()

  for (let minutes = 0; minutes < 60; minutes += interval) {
    values.add(minutes)
  }

  values.add(selectedMinute.value)

  return Array.from(values)
    .sort((a, b) => a - b)
    .map(value => ({
      value,
      label: zeroPad(value),
      active: value === selectedMinute.value,
      disabled: !isAllowedTime(selectedHour24.value, value),
    }))
})

const periodOptions = computed(() => ['AM', 'PM'].map(value => ({
  value,
  label: value,
  active: selectedPeriod.value === value,
  disabled: !hasAllowedMinuteForHour(togglePeriodHour(selectedHour24.value, value)),
})))

const timeValue = computed({
  get(): any | undefined {
    if (props.modelValue == null || props.modelValue === '') return undefined

    const aug = props.augmenter as any
    let date: any = props.modelValue

    if (aug && aug.input) {
      date = aug.input(props.modelValue)
    }

    if (date instanceof Date) {
      return new Time(date.getHours(), date.getMinutes())
    }

    if (typeof date === 'number') {
      const hours = Math.floor(date / 60)
      const minutes = date % 60
      return new Time(hours, minutes)
    }

    return undefined
  },
  set(value: any | undefined) {
    updateTimeValue(value)
  },
})

function updateTimeValue(value: any | undefined) {
  if (!value) {
    emit('update:modelValue', null)
    emit('change')
    return
  }

  const aug = props.augmenter as any
  const date = new Date(2000, 0, 1, value.hour, value.minute, 0, 0)

  if (aug && aug.output) {
    emit('update:modelValue', aug.output(date))
  } else {
    emit('update:modelValue', date)
  }
  emit('change')
}

function selectTime(minutes: number) {
  if (usesMinutesAugmenter.value) {
    emit('update:modelValue', minutes)
    emit('change')
  } else {
    updateTimeValue(new Time(Math.floor(minutes / 60) % 24, minutes % 60))
  }
}

function selectHour(hour: number) {
  const hour24 = is24hr.value ? hour : hour12To24(hour, selectedPeriod.value)
  selectPickerTime(hour24, selectedMinute.value)
}

function selectMinute(minute: number) {
  selectPickerTime(selectedHour24.value, minute)
}

function selectPeriod(period: string) {
  selectPickerTime(togglePeriodHour(selectedHour24.value, period), selectedMinute.value)
}

function selectPickerTime(hour: number, minute: number) {
  const min = Math.min(minOptionMinutes.value, maxOptionMinutes.value)
  const max = Math.max(minOptionMinutes.value, maxOptionMinutes.value)
  const minutes = Math.min(Math.max((hour * 60) + minute, min), max)

  selectTime(normalizeMinutes(minutes))
}

function valueToMinutes(value: Props['modelValue']): number | null {
  if (value == null || value === '') return null

  if (typeof value === 'number' && usesMinutesAugmenter.value) {
    return normalizeMinutes(value)
  }

  const aug = props.augmenter as any
  let date: any = value

  if (aug && aug.input) {
    date = aug.input(value)
  }

  if (date instanceof Date) {
    return normalizeMinutes((date.getHours() * 60) + date.getMinutes())
  }

  if (typeof date === 'number') {
    return normalizeMinutes(date)
  }

  return null
}

function normalizeOptionBoundary(value: Props['minTime'] | Props['maxTime'], fallback: number): number {
  if (value == null || value === '') return fallback

  if (value instanceof Date) {
    return normalizeMinutes((value.getHours() * 60) + value.getMinutes())
  }

  const numeric = Number(value)
  if (Number.isFinite(numeric)) {
    return normalizeMinutes(numeric)
  }

  return fallback
}

function normalizeMinutes(value: number): number {
  return Math.min(Math.max(Math.round(value), 0), 1440)
}

function isAllowedTime(hour: number, minute: number): boolean {
  const minutes = (hour * 60) + minute
  const min = Math.min(minOptionMinutes.value, maxOptionMinutes.value)
  const max = Math.max(minOptionMinutes.value, maxOptionMinutes.value)

  return minutes >= min && minutes <= max
}

function hasAllowedMinuteForHour(hour: number): boolean {
  const min = Math.min(minOptionMinutes.value, maxOptionMinutes.value)
  const max = Math.max(minOptionMinutes.value, maxOptionMinutes.value)
  const start = hour * 60
  const end = start + 59

  return end >= min && start <= max
}

function hour12To24(hour: number, period: string): number {
  const normalizedHour = hour === 12 ? 0 : hour
  return period === 'PM' ? normalizedHour + 12 : normalizedHour
}

function togglePeriodHour(hour: number, period: string): number {
  if (period === 'AM') return hour >= 12 ? hour - 12 : hour
  return hour < 12 ? hour + 12 : hour
}

function zeroPad(value: number): string {
  return value.toString().padStart(2, '0')
}

function pickerButtonClass(active: boolean, disabled: boolean) {
  if (disabled) {
    return 'cursor-not-allowed text-gray-300 dark:text-gray-700'
  }

  if (active) {
    return 'bg-primary-50 font-medium text-primary-600 dark:bg-primary-950 dark:text-primary-400'
  }

  return 'text-gray-700 hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus:bg-gray-800'
}
</script>
